// useLiveConversation — subscribe to the server's per-conversation SSE stream
// so a second observer on the same thread (e.g. a co-agent Betsy invited)
// sees each new message the instant it's persisted, without refresh.
//
// Backend endpoint (context-specific):
//   GET /api/conversations/{id}/live                            (Sanctuary)
//   GET /api/vessels/{vessel_id}/conversations/{id}/live        (Vessels)
//
// Auto-reconnects on network/proxy drops with exponential backoff. Sends
// `onMessage(msg)` for every persisted message the server publishes; the
// consumer is responsible for deduping by `message_id` against its own
// local state (a browser that just SENT a message will also receive its
// own echo — that's fine and correct, dedupe handles it).

import { useEffect, useRef } from 'react';
import authUtils from '../utils/authUtils';

export function useLiveConversation({ url, enabled = true, onMessage, onStatus, onPresence }) {
  const cancelRef = useRef(false);
  const readerRef = useRef(null);
  
  useEffect(() => {
    if (!enabled || !url) return undefined;
    cancelRef.current = false;
    let backoffMs = 1000;
    const maxBackoffMs = 15000;
    
    const connect = async () => {
      while (!cancelRef.current) {
        try {
          onStatus?.('connecting');
          const res = await authUtils.authFetch(url);
          if (!res.ok || !res.body) {
            throw new Error(`live stream HTTP ${res.status}`);
          }
          onStatus?.('open');
          backoffMs = 1000; // reset on successful connect
          const reader = res.body.getReader();
          readerRef.current = reader;
          const decoder = new TextDecoder();
          let buf = '';
          while (!cancelRef.current) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() || ''; // keep incomplete tail
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'message' && evt.message) {
                  onMessage?.(evt.message);
                } else if (evt.type === 'presence') {
                  onPresence?.(evt.count);
                } else if (evt.type === 'handshake' && typeof evt.presence === 'number') {
                  onPresence?.(evt.presence);
                }
              } catch (_) { /* ignore malformed frame */ }
            }
          }
        } catch (err) {
          onStatus?.('reconnecting');
          // eslint-disable-next-line no-console
          console.debug('[live] disconnected, reconnecting in', backoffMs, 'ms —', err.message);
        }
        if (cancelRef.current) return;
        // Exponential backoff with jitter to avoid thundering herd on
        // shared network hiccups
        const jitter = 0.5 + Math.random();
        await new Promise(r => setTimeout(r, Math.min(maxBackoffMs, backoffMs) * jitter));
        backoffMs = Math.min(maxBackoffMs, backoffMs * 2);
      }
    };
    
    connect();
    
    return () => {
      cancelRef.current = true;
      try { readerRef.current?.cancel(); } catch { /* ignore */ }
      readerRef.current = null;
      onStatus?.('closed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled]);
}
